import { Plugin, reactive } from 'vue'

/**
 * Exposes the current breakpoint name as `$mq` (what vue-mq used to do).
 * Each breakpoint runs up to, but not including, its value.
 */
const breakpoints = {
  xs: 575,
  sm: 768,
  md: 992,
  lg: 1199,
  xl: Infinity,
}

export type Breakpoint = keyof typeof breakpoints

const state = reactive({ current: 'xl' as Breakpoint })

declare module 'vue' {
  interface ComponentCustomProperties {
    readonly $mq: Breakpoint
  }
}

export default {
  install(app) {
    /*
    Only each breakpoint's start is queried, and the widest one that matches wins, so every width has exactly one
    breakpoint. Ranges ending at max-width (next - 1px) missed fractional widths such as 767.5px, which browser zoom
    produces, and left $mq on its previous value.
    */
    const names = Object.keys(breakpoints) as Breakpoint[]
    const starts = names.map((name, index) => ({
      name,
      query: window.matchMedia(`(min-width: ${index === 0 ? 0 : breakpoints[names[index - 1]]}px)`),
    }))
    const update = () => {
      state.current = [...starts].reverse().find(start => start.query.matches)?.name ?? names[0]
    }
    starts.forEach(start => start.query.addEventListener('change', update))
    update()

    Object.defineProperty(app.config.globalProperties, '$mq', {
      get: () => state.current,
    })
  },
} as Plugin
